import React, { useState, useRef, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Upload, FileText, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../common/Button';
import { useToast } from '../common/Toast';
import ModalPortal from '../common/ModalPortal';

interface ResumeUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface ProcessingStep {
  id: string;
  label: string;
  status: 'pending' | 'processing' | 'completed' | 'error';
  duration: number; // in milliseconds
}

const PROCESSING_STEPS: ProcessingStep[] = [
  { id: 'upload', label: 'Uploading your resume', status: 'pending', duration: 4000 }, // 4 sec
  { id: 'parse', label: 'Parsing resume content', status: 'pending', duration: 10000 }, // 10 sec (main parsing time)
  { id: 'extract', label: 'Extracting key details', status: 'pending', duration: 10000 }, //  10 sec
  { id: 'validate', label: 'Validating information', status: 'pending', duration: 10000 }, // 10 sec
  { id: 'save', label: 'Saving to your profile', status: 'pending', duration: 5000 }, // 5 sec (database save)
];

export const ResumeUploadModal: React.FC<ResumeUploadModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingSteps, setProcessingSteps] = useState<ProcessingStep[]>(PROCESSING_STEPS);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [error, setError] = useState<string>('');
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showToast } = useToast();

  // Reset modal state when opened/closed
  React.useEffect(() => {
    if (isOpen) {
      setSelectedFile(null);
      setIsProcessing(false);
      setProcessingSteps(PROCESSING_STEPS);
      setCurrentStepIndex(0);
      setError('');
      setIsDragOver(false);
    }
  }, [isOpen]);

  const validateFile = (file: File): string | null => {
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!allowedTypes.includes(file.type)) {
      return 'Please upload a PDF, JPG, or PNG file.';
    }

    if (file.size > maxSize) {
      return 'File size must be less than 10MB.';
    }

    return null;
  };

  const handleFileSelect = useCallback((file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSelectedFile(file);
    setError('');
  }, []);

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);

    const file = event.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const updateStepStatus = (stepIndex: number, status: ProcessingStep['status']) => {
    setProcessingSteps(prev => 
      prev.map((step, index) => 
        index === stepIndex ? { ...step, status } : step
      )
    );
  };

  const simulateProcessingSteps = async (actualProcessingPromise: Promise<any>) => {
    try {
      // Step 1: Upload (3 seconds)
      setCurrentStepIndex(0);
      updateStepStatus(0, 'processing');
      await new Promise(resolve => setTimeout(resolve, PROCESSING_STEPS[0].duration));
      updateStepStatus(0, 'completed');

      // Step 2: Parse (40 seconds - main processing time)
      setCurrentStepIndex(1);
      updateStepStatus(1, 'processing');
      
      // Start the actual processing and wait for it to complete
      const result = await actualProcessingPromise;
      
      updateStepStatus(1, 'completed');

      // Step 3: Extract (5 seconds)
      setCurrentStepIndex(2);
      updateStepStatus(2, 'processing');
      await new Promise(resolve => setTimeout(resolve, PROCESSING_STEPS[2].duration));
      updateStepStatus(2, 'completed');

      // Step 4: Validate (3 seconds)
      setCurrentStepIndex(3);
      updateStepStatus(3, 'processing');
      await new Promise(resolve => setTimeout(resolve, PROCESSING_STEPS[3].duration));
      updateStepStatus(3, 'completed');

      // Step 5: Save (4 seconds)
      setCurrentStepIndex(4);
      updateStepStatus(4, 'processing');
      await new Promise(resolve => setTimeout(resolve, PROCESSING_STEPS[4].duration));
      updateStepStatus(4, 'completed');

      return result;
    } catch (error) {
      // Mark current step as error
      updateStepStatus(currentStepIndex, 'error');
      throw error;
    }
  };

  const uploadResume = async () => {
    if (!selectedFile) return;

    setIsProcessing(true);
    setError('');

    try {
      // Import apiClient dynamically to avoid circular dependencies
      const { default: apiClient } = await import('../../services/apiClient');

      // This promise represents the single, atomic API call to upload, parse, and create the resume.
      const actualProcessingPromise = () => {
        const formData = new FormData();
        formData.append('file', selectedFile);

        console.log('Calling API to upload, parse, and create resume...');
        // The backend now handles the entire workflow with this single, robust endpoint.
        return apiClient.jobprotRequest(
          '/resumes/parse/upload',
          'POST',
          formData,
          true,
        );
      };

      // Start processing simulation with the actual API call.
      // Errors from the promise will be caught by the outer catch block.
      const result = await simulateProcessingSteps(actualProcessingPromise());

      console.log('Resume upload and creation completed:', result);

      // Show success message
      showToast('Resume uploaded and processed successfully!', 'success');
      
      // Close modal and trigger refresh
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Resume upload error:', error);

      let errorMessage = 'Failed to upload resume. Please try again.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      onClose();
    }
  };

  const getStepIcon = (step: ProcessingStep, index: number) => {
    if (step.status === 'completed') {
      return <CheckCircle size={20} className="text-success-light dark:text-success-dark" />;
    } else if (step.status === 'error') {
      return <AlertCircle size={20} className="text-error-light dark:text-error-dark" />;
    } else if (step.status === 'processing' && index === currentStepIndex) {
      return null; // No icon for processing step, we'll show dots instead
    } else {
      return <div className="w-5 h-5 rounded-full border-2 border-border-light dark:border-border-dark opacity-50" />;
    }
  };

  const getVisibleSteps = () => {
    if (!isProcessing) return [];
    
    const visibleSteps = [];
    const start = Math.max(0, currentStepIndex - 2);
    const end = Math.min(processingSteps.length, currentStepIndex + 3);
    
    for (let i = start; i < end; i++) {
      visibleSteps.push({
        ...processingSteps[i],
        index: i,
        isCurrentStep: i === currentStepIndex,
        distance: Math.abs(i - currentStepIndex)
      });
    }
    
    return visibleSteps;
  };

  if (!isOpen) return null;

  return (
    <ModalPortal>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          >
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -10, opacity: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className="bg-background-light dark:bg-background-dark rounded-xl border border-border-light dark:border-border-dark shadow-lg max-w-md w-full max-h-[90vh] overflow-y-auto"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-border-light dark:border-border-dark">
                <h2 className="text-xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark">
                  Upload Resume
                </h2>
                <X 
                  size={20} 
                  className="cursor-pointer text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark transition-colors" 
                  onClick={handleClose} 
                />
              </div>

              {/* Content */}
              <div className="p-4 md:p-6">
                {!isProcessing ? (
                  <>
                    {/* File Upload Area */}
                    <div
                      className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 ${
                        isDragOver
                          ? 'border-primary-light dark:border-primary-dark bg-primary-light/5 dark:bg-primary-dark/5'
                          : error
                          ? 'border-red-300 dark:border-red-600 bg-red-50/50 dark:bg-red-900/20'
                          : selectedFile
                          ? 'border-success-light dark:border-success-dark bg-success-light/5 dark:bg-success-dark/5'
                          : 'border-border-light dark:border-border-dark hover:border-primary-light/50 dark:hover:border-primary-dark/50'
                      } cursor-pointer`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handleFileInputChange}
                        className="hidden"
                      />

                      {selectedFile ? (
                        <div className="space-y-3">
                          <FileText size={48} className="mx-auto text-primary-light dark:text-primary-dark" />
                          <div>
                            <p className="font-medium text-text-primary-light dark:text-text-primary-dark truncate">
                              {selectedFile.name.length > 24 ? selectedFile.name.slice(0, 24) + '...pdf' : selectedFile.name}
                            </p>
                            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                              {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                            </p>
                          </div>
                          <p className="text-sm text-secondary-light dark:text-secondary-dark">
                            File ready to upload
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {/* Upload Resume GIF */}
                          <div className="mx-auto w-28 h-28 flex items-center justify-center -mt-8">
                            <img
                              src="/upload-resume.gif" 
                              alt="Upload Resume" 
                              className="w-full h-full object-contain"
                              onError={(e) => {
                                // Fallback to icon if GIF doesn't load
                                e.currentTarget.style.display = 'none';
                                e.currentTarget.nextElementSibling?.classList.remove('hidden');
                              }}
                            />
                            <Upload size={48} className="mx-auto text-text-secondary-light dark:text-text-secondary-dark hidden" />
                          </div>
                          <div>
                            <p className="font-medium text-text-primary-light dark:text-text-primary-dark mb-1 -mt-4">
                              Drop your resume here or click to browse
                            </p>
                            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                              Supports PDF, JPG, PNG (max 10MB)
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Error Message */}
                    {error && (
                      <div className="mt-4 p-4 bg-red-50/80 dark:bg-red-900/30 rounded-lg">
                        <div className="flex items-start gap-3">
                          <AlertCircle size={18} className="text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-red-700 dark:text-red-300 font-medium">{error}</p>
                        </div>
                      </div>
                    )}

                    {/* Upload Button */}
                    <div className="mt-6 flex flex-col md:flex-row justify-center gap-3 w-full flex-col-reverse md:flex-row">
                      <Button
                        variant="secondary"
                        onClick={handleClose}
                        disabled={isProcessing}
                        className="w-full md:max-w-xs p-8"
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        onClick={uploadResume}
                        disabled={!selectedFile || isProcessing}
                        leftIcon={<Upload size={16} />}
                        className="w-full md:max-w-xs p-8"
                      >
                        Upload Resume
                      </Button>
                    </div>
                  </>
                ) : (
                  /* Processing UI - Lottery Spin Style */
                  <div className="space-y-6">
                    {/* Processing Header */}
                    <div className="text-center">
                      <div className="w-16 h-16 mx-auto mb-4 bg-primary-light/10 dark:bg-primary-dark/10 rounded-full flex items-center justify-center">
                        <Loader2 size={32} className="animate-spin text-primary-light dark:text-primary-dark" />
                      </div>
                      <h3 className="text-lg font-heading font-semibold text-text-primary-light dark:text-text-primary-dark mb-2">
                        Processing Your Resume
                      </h3>
                      <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                        Please wait while we analyze and extract information from your resume
                      </p>
                    </div>

                    {/* Lottery Spin Steps */}
                    <div className="relative h-36 overflow-hidden">
                      <div className="absolute inset-0 flex flex-col justify-center space-y-4">
                        {getVisibleSteps().map((step) => (
                          <motion.div
                            key={step.index}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ 
                              opacity: step.isCurrentStep ? 1 : 0.4,
                              y: 0,
                              filter: step.isCurrentStep ? 'blur(0px)' : `blur(${step.distance * 2}px)`,
                              scale: step.isCurrentStep ? 1 : 0.95
                            }}
                            transition={{ duration: 0.5 }}
                            className={`flex items-center gap-3 justify-center ${
                              step.isCurrentStep ? 'text-primary-light dark:text-primary-dark' : 'text-text-secondary-light dark:text-text-secondary-dark'
                            }`}
                          >
                            {getStepIcon(step, step.index)}
                            {step.isCurrentStep && step.status === 'processing' && (
                              <div className="flex space-x-1">
                                <div className="w-1 h-1 bg-primary-light dark:bg-primary-dark rounded-full animate-pulse"></div>
                                <div className="w-1 h-1 bg-primary-light dark:bg-primary-dark rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                                <div className="w-1 h-1 bg-primary-light dark:bg-primary-dark rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                              </div>
                            )}
                            <span className={`text-sm font-medium ${step.isCurrentStep ? 'text-primary-light dark:text-primary-dark' : ''}`}>
                              {step.label}
                            </span>
                          </motion.div>
                        ))}
                      </div>
                    </div>

                    {/* Error Message during processing */}
                    {error && (
                      <div className="p-4 bg-red-50/80 dark:bg-red-900/30 rounded-lg">
                        <div className="flex items-start gap-3">
                          <AlertCircle size={18} className="text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-red-700 dark:text-red-300 mb-1">
                              Processing Failed
                            </p>
                            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                          </div>
                        </div>
                        <div className="mt-3 flex justify-end">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={handleClose}
                          >
                            Close
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </ModalPortal>
  );
}; 